/**
 * Created by mauget on 2/8/16.
 */

// ConnectionController.js

import fabric       from 'fabric.js/dist/fabric.require';
import constants    from 'Constants.js';
import listService  from 'ListService.js';
import { ConnectionArrowClass } from 'shapelib/ConnectionArrowClass.js';
import LineModel from 'LineModel.js';
import ConnectionVO from 'ConnectionVO.js';
import undoService from 'UndoService.js'
import crudController   from 'CrudController';
import textConstants    from 'TextConstants';
import _ from 'lodash';
import objectUtil from 'util/ObjectUtil';
import widgetUtil from 'util/WidgetUtil';
import util from '../service/util/Util.js';

//===========================================================================================
// An event-driven state machine mechanism to drive interactive rendering of a behavior
// connection. This is a singleton that has state. This is okay; there can be, at most, only
// one active user-interaction with behaviors or workflow connections in the system.
//
// Lou Mauget, February 8, 2016
//-------------------------------------------------------------------------------------------
// A state machine transforms event-driven stateful spaghetti to ziti. The goal is to
// track the connection-drawing stage of the user / mouse, giving appropriate viaual
// cues.
//
// When the mouse hovers over a connection point, it glows. If the user presses the
// button, line drawing begins, extinguishing the connection point. As the user draws, using
// the depressed mouse, a line emerges from the connection point to the mouse. If the mouse
// moves over a new connection point in another widget, that potential destination point
// glows. If the user releases the mouse, a new, permanent connection line appears between
// source and destination points. An arrow appears at the destination end of the line.
//
// Normal connection-drawing state proceeds from value 0 .. 5. Indented remarks are action
// or connection-abort  steps:
//
//  DEFCON_0 (user not doing anything connection-wise)
//  DEFCON_1 (user moved the mouse over a connection point)
//          (move to DEFCON_0 if mouse moved off point with mouse up)
//  DEFCON_2 (mouse-down over connection point)
//  DEFCON_3 (mouse-down continues; now moving from connection point; drawing a line)
//          (move to STATE_O if mouse-up during this line-drawing state)
//  DEFCON_4 (encouncterd mouse-up in DEFCON_3 over second connection point)
//          (create line anchored in each connection endpoint, move to DEFCON_5)
//  DEFCON_5 (connection has been carried out -- just a quick interception state )
//          (housekeeping, then move to 0)
//  DEFCON_6 (housekeeping to begin to reconnect a line)
//
// Note that ES2015 Symbols contain strings used only for human identification via logs or
// debuggers.
//
// Note2: Maybe we could have used reactivex to eliminate the use of a state machine.
//
// Some complexity because:
//  (1) FabricJs hit-detects a group, but not individual member objects.
//  (2) fabricJS move-with mouse-down doesn't fire mouse-over events, so we have to
//      enumerate fabric objects, hit-testing them ourselves during line-drawing.
//  (3) If we get a group hit in (2) we have to deal with (1) to detect a connection point
//      within that group.
//
// Sidebar: DEFCON is a term from the 1983 movie, "War Games." see
// http://www.imdb.com/title/tt0086567/?ref_=fn_al_tt_1
// DEFCON_5 would be "Defense condition five" -- prepare for total annihilation.
//
// PF-2576: Should  move from DEFCON_4 to DEFCON_5 if and only if mouse up during DEFCON_4
//
//===========================================================================================
const DEFCON_0 = Symbol('0 quiescent');
const DEFCON_1 = Symbol('1 on a connection point');
const DEFCON_2 = Symbol('2 mouse down ON source connection point');
const DEFCON_3 = Symbol('3 mouse_down & mouse-moving ==> is_drawing');
const DEFCON_4 = Symbol('4 create a permanent line after mouse-up');
const DEFCON_5 = Symbol('5 housekeeping after creating permanent line');
const DEFCON_6 = Symbol('6 initialize moving a line');

const SILENT = { silent: true };
const EMPTY  = constants.STRING_EMPTY;

class ConnectionController {

    constructor() {
        this.debug = false;  // @lem falsify before push
        this.state = DEFCON_0;
        this.srcGroup = undefined;
        this.srcDot   = undefined;
        this.destDot  = undefined;

        // Cached feeback line used to repaint feedback line after seting a dot visible.
        this.prevFeedbackLineCoords = undefined;

        // Set: enables DEFCON_4, thus making a connection. Mouse-up over a destination dot sets this flag.
        this._srcGroupConnPending = undefined;
        this._isConnectionGlued = undefined;
        this.reconnectingLine = undefined;
        this.reconnectingEnd = undefined;
        this.reconnectingLineModel = undefined;
    }

    //====================================================================
    // App-wide available constant
    //====================================================================
    get DEFCON_0() { return DEFCON_0; }


    //====================================================================
    // System-wide, stateful properties
    //====================================================================
    get srcGroupConnPending() { return this._srcGroupConnPending; }
    set srcGroupConnPending(arg) {
        this._srcGroupConnPending = arg;
    }

    get isConnectionGlued() { return this._isConnectionGlued; }
    set isConnectionGlued(arg) {
        this._isConnectionGlued = arg;
    }

    get state() { return this._state; }
    set state(arg) {
        this._state = arg;
    }

    get srcGroup() { return this._srcGroup; }
    set srcGroup(arg) {
        this._srcGroup = arg;
    }

    get srcDot() { return this._srcDot; }
    set srcDot( arg ) { this._srcDot = arg; }

    get destDot() { return this._destDot; }
    set destDot( arg ) { this._destDot = arg; }

    get connectionRadius() { return listService.mainKind === constants.KIND_BEHAVIORS
        ? constants.CONNECT_PT_RADIUS : constants.CONNECT_PT_RADIUS_WRKFLW ; }

    get debug() { return this._isDebugMode; }
    set debug( arg ) { this._isDebugMode = arg; }


    //====================================================================
    // Locks shape from being dragged ... unlocks if isLock is false.
    //====================================================================
    _lockMovement( obj, isLock = true ) {
        if (obj) {
            // Exclude connection objects from having their lock status change
            if(obj.type !== ConnectionArrowClass.prototype.type) {
                obj.lockMovementX = isLock;
                obj.lockMovementY = isLock;
            } else {
                obj.lockMovementX = true;
                obj.lockMovementY = true;
            }

            // if is Selection Group
            if (!obj.model) {
                //loop through objects in group and unlock.
                _.each(obj._objects, function(item) {
                    item.lockMovementX = isLock;
                    item.lockMovementY = isLock;
                });
            }


        }
    }


    //====================================================================
    // Redraws a user-driven feedback connection line
    //====================================================================
    _drawFeedbackLine(canvas, coords ) {

        canvas.hoverCursor = 'pointer'; // aka pointing finger

        const ctx = canvas.getContext('2d');

        ctx.strokeStyle = constants.CONNECTION_LINE_1;
        ctx.lineWidth = 0.25;

        ctx.beginPath();

        ctx.moveTo(coords.x1, coords.y1);
        ctx.lineTo(coords.x2, coords.y2);

        ctx.closePath();
        ctx.stroke();

        // Lagging coords cached to allow redrawing line just after lighting a dot clears line from the canvas.
        this.prevFeedbackLineCoords = coords;
    }


    //====================================================================
    // We should be in DEFCON_0 when looking for a chance to draw a line.
    //====================================================================
    _setStartState() {
        this.state = DEFCON_0;

        this.srcGroup = undefined;
        this.srcDot = undefined;
        this.destGroup = undefined;
        this.destDot = undefined;

        // Cached last-feedback line used to repaint the feedback line if its image was dropped during hiding a dot.
        this.prevFeedbackLineCoords = undefined;

        // When set, this value means DEFCON_3 has armed a potential connection to a destination.
        // Think of it was a DEFCON_3.5. If set, the The next mouse-up during DEFCON_3, we will transition state to
        // DEFCON_4, meaning "connect source to destination with an arrow".
        //
        // If a mouse-over determines, however,  that the pointer is no longer over a legal destination, than this
        // value should be set undefined so that the DEFCON_3 state continues to seek a new legal destinatin point
        // during mouse-over events.
        this.srcGroupConnPending = undefined;
    }


    //====================================================================
    // Pass each fabricjs group to supplied callback function.
    //====================================================================
    _enumerateGroups( canvas, callback ) {

        canvas.getObjects().map( ( obj ) => {
            if (obj.type === 'group') {
                callback( obj );
            }
        });
    }


    //====================================================================
    // Create a permanent connection line object between the source dot
    // and the destination dot.
    // Returns the line for associating by its anchor widgets.
    //====================================================================
    _drawConnection( canvas, src, dest  ) {

        if (!src){
            return;
        }

        const strokeWidth = constants.CONN_STROKE_WIDTH; //PF-5194 Giving more width to the stroke so the click does not have to be exactly on the line
        const lineOffset = strokeWidth / 2; //Offset created by the stroke width
        const c = 2 * this.connectionRadius - lineOffset; // .. radius of orange dot
        const coords = [  src.xl + c, src.yt +  c, dest.xl + c, dest.yt + c ];

        // Prevent an invisible line
        if (coords[0] === coords[2]) {
            coords[2]+= 0.5;
        }
        if (coords[1] === coords[3]) {
            coords[3]+= 0.5;
        }

        const w1 = this.srcGroup;
        const w2 = this.destGroup;
        const
            src2 = this.findBBObjectById(w1.model.get('id')),
            dest2 = this.findBBObjectById(w2.model.get('id'));

        if (!dest2 || !src2 ) {
            return undefined;
        }

        const model = new LineModel( { initCoords: coords, name: '', srcDot: src.dot, destDot: dest.dot, text: '', textWidth: 0 });

        // Handle a moved path here:
        if (this.reconnectingLineModel && this.reconnectingLineModel.attributes) {
            widgetUtil.removeAllBezeierControlPoints();
            listService.currentSelectedPathId = undefined;

            const attributes = Object.assign({}, this.reconnectingLineModel.attributes);
            for (const p in attributes) {
                if (p !== 'id' && attributes[p] !== undefined && attributes[p] !== null) {

                    [1, 2].forEach(n => delete attributes[`controlPoint${n}`]);

                    if (p === 'destWidgetModel' || p === 'srcWidgetModel') {
                        model.attributes[p] = Object.assign({}, attributes[p]);
                    } else if (p === 'model') {
                        // Sever recursive connection_arrow model. New one created on moved path anyway.
                        delete model.attributes[p];
                    } else {
                         // Selective / recursive copy except for new points
                         if(!['srcDot','destDot', 'self'].includes(p)){
                            model.attributes[p] = objectUtil.advancedCopy(attributes[p]);
                        }
                    }
                }
            }
        }

        const locked = true;
        const line = new ConnectionArrowClass(coords, {
            fill:          constants.CONNECTION_LINE_1,
            stroke:        constants.CONNECTION_LINE_1,
            strokeWidth:   strokeWidth,
            lockMovementX: locked,
            lockMovementY: locked,
            lockRotation:  locked,
            selectable:    true,
            hasControls:   false,
            hasBorders:    false,
            model:         model,
            visible:       true
        });

        canvas.add( line );

        // Record original points and record dot / connection-point within widget, for each end.
        line.model = model;
        line.model.attributes.layoutKind = listService.mainKind; // (i.e. Behaviors or Workflow)
        line.setCoords();
        return line;

    }

    _checkConnectionAvailable(target) {
        let connSize = 1;
        let availableConnection = true;

        // Normal bets are off during initial steps of reconnection. Maty's validation should catch at the end.
        if (!this.reconnectingLine) {
            // Simple branch can only have 2 connections
            if (target.model.attributes.type === 'branchcommand') {
                connSize = 2;
            }
            // Multiple branch can have many connections
            else if (target.model.attributes.type === 'casecommand') {
                connSize = -1;
            }
            if (connSize > 0) {
                let count = 0;
                for (let connIndex in target.connection) {
                    if (target.connection[connIndex].end === constants.A_END) {
                        count++
                    }
                    if (count === connSize) {
                        availableConnection = false;
                        break;
                    }
                }
            }
        }
        return availableConnection;
    }

    //=======================================================================
    // Conditionally sets state, if mouse is atop a connection point.
    // If we're in DEFCON_3 and mouse is over a new connection dot,
    // set DEFCON_4 (pending a mouse-up).
    //
    // "New" means source group with source dot differing from current group
    // and dot.
    //
    // TODO This brittle mutating test needs to be refactored into a read-only test.
    // (It is recursivley called at times, changing flags and states.)
    //=======================================================================
    _testHitConnectionPoint(canvas, target, mpos ) {
        let hit = undefined;

        if (target && target.type === 'group' &&  target.containsPoint( mpos ) ) {

            // Move past the current connection point when reconnecting
            if(this.state === DEFCON_3 && this.reconnectingLine && this.srcDot && this.destDot) {
                if( !this.movedPast ) {

                    if(this.reverseFlag &&
                        this.srcDot &&
                        !(mpos.x >= this.srcDot.xl && mpos.x <= this.srcDot.xr &&
                            mpos.y >= this.srcDot.yt && mpos.y <= this.srcDot.yb)) {

                        this.movedPast = true;
                    }
                    else if (!this.reverseFlag && this.destDot &&
                        !(mpos.x >= this.destDot.xl && mpos.x <= this.destDot.xr &&
                            mpos.y >= this.destDot.yt && mpos.y <= this.destDot.yb)) {

                        this.movedPast = true;
                    }
                    else {
                        return undefined;
                    }
                }
            }


            const width  = target.getWidth();
            const height = target.getHeight();

            const grpPos = {x:target.left, y:target.top};

            canvas.hoverCursor = 'pointer'; // aka pointing finger

            // Hit testing on connector dots. If one contains pointer, light it, lock movement and quit testing
            target.forEachObject((obj, index) => {

                // Skip base widget
                if (index === 0){
                    return;
                }

                // Clear each dot initially, but don't render yet
                obj.setFill( constants.CONNECTION_CLR_CLEAR );

                const objPos = {
                    xl: ( grpPos.x + obj.left + width / 2 - 1 * this.connectionRadius ),
                    xr: ( grpPos.x + obj.left + width / 2 + 4 * this.connectionRadius ),

                    yt: ( grpPos.y + obj.top + height / 2 - 1 * this.connectionRadius ),
                    yb: ( grpPos.y + obj.top + height / 2 + 4 * this.connectionRadius )
                };

                if (mpos.x > objPos.xl && mpos.x < objPos.xr &&
                    mpos.y > objPos.yt && mpos.y < objPos.yb) {

                    // Already drawing?
                    if (this.state !== DEFCON_3 && !this.reverseFlag) {

                        this._lockMovement(target, true);
                        this.state = DEFCON_1;

                        this.srcGroup = target;
                        this.srcDot = objPos;

                        // Source dot hit
                        hit = this.srcDot;
                        hit.dot = index - 1; // to match PF index

                        //console.log(`srcDot`, hit.dot);
                    } else if (this.reverseFlag) {
                        const availableConnection = (this.state === DEFCON_3) ?
                            this._checkConnectionAvailable(target) : true;

                        if (availableConnection) {

                            // Same as prior if but without setting of state
                            this._lockMovement(target, true);

                            this.srcGroup = target;
                            this.srcDot = objPos;

                            // Source dot hit
                            hit = this.srcDot;
                            hit.dot = index - 1; // to match PF index

                            // Don't set state here: you'll get phantom lines on mouse-over connection dots.
                            // this.state = DEFCON_3; // @lem

                            if (this.debug) { console.log(`srcDot`, hit.dot); }
                        }
                    } else {
                        // Dest dot hit
                        this.destDot = objPos;
                        this.destGroup = target;

                        hit = this.destDot;
                        hit.dot = index - 1; // to match PF index

                        this.state = DEFCON_3; // @lem
                        this.srcGroupConnPending = this.srcGroup;
                    }

                    // Light source or destination point, according to state.
                    obj.setFill(constants.CONNECTION_CLR_HOVER);
                    canvas.renderAll();

                    // Use feedback line cached coords to replace feedback cleared by renderall
                    if (this.prevFeedbackLineCoords) {
                        this._drawFeedbackLine(canvas, this.prevFeedbackLineCoords);
                        this.prevFeedbackLineCoords = undefined;
                    }
                }

            });

            // We're atop a group, but not atop a connection dot of a group
            if (!hit) {
                if (!this.reconnectingLine) {
                    // Reset state on widget
                    if (target && target.state) {
                        if (target.state === DEFCON_6) {
                            return undefined;
                        }
                        target.state = undefined;
                    }
                    //this._lockMovement(target, false);
                    if (this.state !== DEFCON_3) {
                        this._setStartState();
                    }
                }
                canvas.trigger('mouse:out', {target: target, drawing: (this.state === DEFCON_3)});

            // TODO: this method could undo any settings via indirect recursion. Sometimes that's good, but it is a maintenance quagmire.
            } else if (hit && canvas._activeObject && canvas._activeObject.type === ConnectionArrowClass.prototype.type) {
                this.state = DEFCON_6;
                this.reconnectingLine = canvas._activeObject;
                this.reconnectingLineModel = this.reconnectingLine.model;
            } else if (hit && this.state === DEFCON_3 && this.reconnectingLine  ) {
                // This setting is one that can change as the user drags the reconnecting line over dots.
                this.srcGroupConnPending = this.srcGroup;
            }
        }

        // if hit defined, hit.index contains dot index
        return hit;
    }

    _condRemoveReconnectingLine(canvas, whichEnd){
        // Only remove line connection from src/target here when we know we reconnected.
        if (this.reconnectingLine && this.srcGroup && this.destGroup &&
            this.isConnectionGlued &&
            this.isValidConnection(this.srcGroup, this.destGroup, this.reconnectingLine) ){

            const widget1 = this.findWidgetById(this.reconnectingLine.srcId);
            const widget2 = this.findWidgetById(this.reconnectingLine.destId);
            // Adding this validation to solve a problem when this.reconnectingLine keeps assigned when you
            // go from behavior to canvas without deselecting the last drawn connection
            if (widget1 && widget1.connection && widget2 && widget2.connection) {

                const mainKind = listService.mainKind;
                // Remove persistence behavior connectionVO or workflow path
                switch ( mainKind ) {
                    case constants.KIND_BEHAVIORS:
                        listService.destroyBehaviorConnnection( this.reconnectingLine.model.get('id') );
                        break;
                    case constants.KIND_WORKFLOW:
                        listService.deleteWorkflowPath( this.reconnectingLine.model.attributes );
                        break;
                }

                // Remove the line from connection array on widgets.
                let self = this;
                function checkLine(line) {
                    return line.line === self.reconnectingLine;
                }
                let index = widget1.connection.findIndex(checkLine);
                widget1.connection.splice(index, 1);
                index = widget2.connection.findIndex(checkLine);
                widget2.connection.splice(index, 1);

                canvas.remove(this.reconnectingLine);
                this.isConnectionGlued = false;
                this.reverseFlag = undefined;
                this.reconnectingLine = undefined;
            }
        }

    }

    //=================================================================
    // Make the connection stick to each endpoint if an endpoint
    // widget is moved.
    // Used by this module and BehaviorsViews
    //=================================================================
    glueLineToEndPoints(w1, w2, line ) {

        // Remove any replaced line first, otherwise, we'd remove the shared end of the reconnect line
        this._condRemoveReconnectingLine( listService.fcanvas );

        // Lazy 1:n connection point in each endpoint widget.
        if (!w1.connection) {
            w1.connection = [];
        }
        if (!w2.connection) {
            w2.connection = [];
        }

        if (!w1.model.get('connections')) {
            w1.model.set('connections', [], SILENT);
        }
        if (!w2.model.get('connections')) {
            w2.model.set('connections', [], SILENT);
        }


        const
            c1 = {line: line, end: constants.A_END},
            c2 = {line: line, end: constants.B_END};

        w1.connection.push( c1 );
        w2.connection.push( c2 );

        this.sinkLine( listService.fcanvas, line);

        this.isConnectionGlued = true;
        if (this.debug) {
            console.log('glue: line *****', line);
            console.log('glue: record connection - w1 = ', w1);
            console.log('glue: record connection - w2 = ', w2);
        }

    }

    //=================================================================
    // Given a fabricjs.point on the canvas, return the widget that
    // contains that point. Returns undefined if none.
    // Used by this module and BehaviorsViews
    //=================================================================
    findWidgetForPoint(canvas, point ){
        let widget = undefined;
        canvas.forEachObject( obj => {
            if (obj.containsPoint( point )){
                widget = obj;
            }
        });
        return widget;
    }

    //==================================================================
    // Get persisted BB objects that wrap the Fabric objects w1, and w2
    //==================================================================
    findBBObjectById(anId) {
        const mainKind = listService.mainKind;
        let widget = undefined;
        if(mainKind === constants.KIND_BEHAVIORS) {
            widget = listService.getBehaviorObject( anId );
        } else {
            widget = listService.getWorkflowStageOrComment( anId );
        }
        return widget;
    }

    //=======================
    // Retrieve widget by ID
    //=======================
    findWidgetById(anId) {
        const anObject = this.findBBObjectById(anId);
        let widget = undefined;
        if(anObject) {
            widget = anObject.model.attributes.widget;
        }
        return widget;
    }


    //=================================================================
    // Send the line to the bottom of the stack of shapes.
    // Gridlines are on their own stacked canvas, so they cannot
    // interfere or be interfereed with.
    //=================================================================
    sinkLine( canvas, line ){
        canvas.sendToBack(line);

    }

    //=================================================================
    // Anchor the line ends at source and destination dots,
    // respectively. Widget for first point anchors A_END.
    // Widget for second point holds B_END of line.
    //
    // Returns boolean true if connection success.
    //
    // Intended to enable updating the position of the line end that
    // a dragged widget anchors.
    //=================================================================
    _recordConnection(canvas, line){
        this.sinkLine(canvas, line);

        //const c = 2 * this.connectionRadius;
        const mainKind = listService.mainKind;
        let connectionVO;
        const p1 = new fabric.Point( line.get('x1'), line.get('y1'));
        const p2 = new fabric.Point( line.get('x2'), line.get('y2'));

        const w1 = this.srcGroup;
        const w2 = this.destGroup;

        const isConnected = w1 && w2;
        if (isConnected) {

            const model = line.model;

            line.srcId = w1.model.get('id');
            line.destId = w2.model.get('id');

            // Record record dot / connectionVO-point, for each end.
            // The dot indices are 0..3, proceeding clockwise from east.
            // eg 0 is east; 1 is south; 2 is west; 3 is north;
            // Get persisted BB objects that wrap the Fabric objects w1, and w2
            const
                src = this.findBBObjectById(w1.model.get('id')),
                dest = this.findBBObjectById(w2.model.get('id'));

            if (!dest || !src ) {
                connectionVO = undefined;
            } else {

                const
                    srcDot  = Number(model.get('srcDot')),
                    destDot = Number(model.get('destDot'));
                //PF-4545 if we are reconecting a line we need to use current Id 
                //because behaviors can be associated to this id  
                const opts = this.reconnectingLineModel ? { id: this.reconnectingLineModel.attributes.id } : {};
                connectionVO = new ConnectionVO( src, dest, srcDot, destDot, opts);
                line.model.attributes.id =  connectionVO.id;
                line.model.attributes.srcWidgetModel = src.model;
                line.model.attributes.destWidgetModel = dest.model;


                if (this.debug) { console.log(`CONNECTION_VO`, connectionVO); }

                connectionVO.model = model;
                this.glueLineToEndPoints( w1, w2, line, model );

                if (this.debug) { console.log(`CONNECTION MODEL`, model ); }

                // Record persistance behavior connectionVO or workflow path
                switch ( mainKind ) {
                    case constants.KIND_BEHAVIORS:
                        listService.addBehaviorConnection( connectionVO );
                        break;
                    case constants.KIND_WORKFLOW:
                        listService.addWorkflowPath( connectionVO );
                        break;
                }

            }
        }
        return connectionVO;
    }

    //=================================================================
    // This method is called by the page / canvas initialization.
    // We use the state machine to make sense of a potential user-
    // drawn connection line..
    // The state machine is global state for the caller.
    //
    // Note that the machine repeats its state filters, driven
    // by mouse listeners that it manages.
    //=================================================================
    runStateMachine(canvas, enable = true) {
        canvas.states = {
            'DEFCON_0': DEFCON_0,
            'DEFCON_1': DEFCON_1,
            'DEFCON_2': DEFCON_2,
            'DEFCON_3': DEFCON_3,
            'DEFCON_4': DEFCON_4,
            'DEFCON_5': DEFCON_5,
            'DEFCON_6': DEFCON_6
        };
        // this.srcGroupConnPending = undefined;

        const _onMouseMove = ( evt ) => {
            const mpos = canvas.getPointer(evt.e);
            let target = evt.target;
            // if (this.debug && target) { console.log(`target`, target); }
            canvas.defaultCursor = 'default';
            if (!this.srcGroupConnPending) {
                // If this is a ConnectionArrow, skip mouse moving logic
                if (target && target.type === ConnectionArrowClass.prototype.type) {
                    return;
                }

                //PF-5194 there are some points in a curved line where the hover was not identified
                //this is used to check if the mouse is hovering over a curved line
                if(!target){
                    widgetUtil.checkCurvedLineIfHovered(evt);
                }

                target = evt.target;

                if (target && target.type === ConnectionArrowClass.prototype.type) {
                    canvas.defaultCursor = 'pointer';
                }
               
                // Has mouse hit a point used for source point? Conditionally begin a connection -- would set DEFCON_2:
                this._testHitConnectionPoint(canvas, target, mpos);

                // Test states in ascending order -- a side effect of an earlier state could set a subsequent state.
                // Event handlers field the interactions. State assigns meaning to them..
                this._defcon2Filter(canvas, evt, mpos);
            }


            this._defcon3Filter( canvas, evt, mpos );
            this._defcon4Filter( canvas, evt, mpos );

        };

        //====================================================================================
        // If over a dot, begin drawing from dot to mouse.
        // This determination is complicated by the fact that the event fires for every group!
        // We use the state to determine what to do next.
        //====================================================================================
        const _onMouseDown = evt => {

            const target = evt.target;
            listService.isElementMoving = true;

            // If this is a ConnectionArrow, skip mouse down logic
            if(target && target.type === ConnectionArrowClass.prototype.type) {
                return;
            }

            this._defcon6Filter( canvas, evt );


            if ( target && this.state === DEFCON_1 ) {

                this._lockMovement( target );

                target.hasBorders = true;

                this.state = DEFCON_2;
            }

            if ( this.state === DEFCON_2) {
                // added per ReconnectLogic
                target.state = this.state;
            }
            else {
                // Mouse-down, but not over a dot? Back to start state:
                this._setStartState();

                if (target) {
                    this._lockMovement( target, false );
                    target.hasBorders = true;
                }
            }

            // Extinguish dot either way.
            canvas.trigger('mouse:out', {target: target});

        };

        // Extinguish all dots
        // If released over a connection point while with pending connection, build and connect line object.
        const _onMouseUp = evt => {
            let target = evt.target;
            listService.isElementMoving = false;

            // Complete connection if DEFCON_3 recorded a dest dot hit. DEFCON_4 is pending, awaiting this mouse-up.
            if (this.srcGroupConnPending) {
                this.state = DEFCON_4;

                // Manually invoke the DEFCON_4 filter
                const mpos = canvas.getPointer(evt.e);
                this._defcon4Filter( canvas, target, mpos );

            } else {

                this.reconnectingLine = undefined;
                this.reconnectingEnd = undefined;
                this.reconnectingLineModel = undefined;
                this.reverseFlag = false;

                // Have made a connection to the dest connection point?
                if (this.state !== DEFCON_5 && target && target.type === 'group') {
                    this._lockMovement(target, false);
                    this._setStartState();
                }
            }

            if(!target){
                const option = {target:target, e: evt.e};
                widgetUtil.checkAllObjectsIfHit(option);
                target = option.target;
            }

            // Extinguish dot
            canvas.trigger('mouse:out', {target: target});
        };

        // Extinguish any lighted connection point
        const _onMouseOut = ( evt ) => {
            const target = evt.target;

            if (!this.srcGroupConnPending) {
                if (target) {
                    target.setFill(constants.CONNECTION_CLR_CLEAR);

                    // If state 0, allow dragging widgets again.
                    if (this.state === DEFCON_0) {
                        this._lockMovement(target, false);
                    }

                    // Clear the group state to allow movement of the lines
                    if (this.srcGroup) {
                        this.srcGroup.state = undefined;
                    }

                    if (!evt.drawing) {
                        canvas.renderAll();
                    }
                }
            }

        };

        canvas.off('mouse:move', _onMouseMove );
        canvas.off('mouse:down', _onMouseDown );
        canvas.off('mouse:up'  , _onMouseUp );
        canvas.off('mouse:out' , _onMouseOut );

        // Restart the logic:
        if (enable) {

            canvas.on('mouse:move', _onMouseMove );
            canvas.on('mouse:down', _onMouseDown );
            canvas.on('mouse:up'  , _onMouseUp );
            canvas.on('mouse:out' , _onMouseOut );
        }
    }

    //=========================================================================================
    // DEFCON_2-sensitive helper function used by event listeners
    //=========================================================================================
    _defcon2Filter( canvas, evt,  mpos ) {
        if (this.state === DEFCON_2) {
            const target = evt.target;
            this._lockMovement( target, true );
            canvas.hoverCursor = 'default'; // aka arrow

            this.state = DEFCON_3;
            this.srcGroup.state = this.state;

     //       this.srcGroupConnPending = undefined;
        }
    }

    //=========================================================================================
    // DEFCON_3-sensitive helper function used by event listeners
    //=========================================================================================
    _defcon3Filter( canvas, evt,  mpos ) {

        if (this.state === DEFCON_3 ) {
            if (this.debug) { console.log(`DEFCON_3 event`, evt); }
            const target = evt.target;

            // DRAW feedback connection line as user draws connection
            let coords = { x1: mpos.x, y1: mpos.y,
                x2: this.srcDot.xl + 2*this.connectionRadius,
                y2: this.srcDot.yt + 2*this.connectionRadius};

            if (this.reconnectingEnd === constants.A_END) {
                coords = { x1: mpos.x, y1: mpos.y,
                    x2: this.destDot.xl + 2*this.connectionRadius,
                    y2: this.destDot.yt + 2*this.connectionRadius};
            }

            this._drawFeedbackLine( canvas, coords );

            this._enumerateGroups( canvas, grp => {

                // Light potential destination dot if atop it
                let validPoint = false;
                if (this.reconnectingEnd) {
                    validPoint = ((this.srcGroup !== grp && this.reconnectingEnd === constants.B_END) ||
                        (this.destGroup !== grp && this.reconnectingEnd === constants.A_END));
                } else {
                    validPoint = (this.srcGroup !== grp);
                }


                if (validPoint && grp.containsPoint( mpos ) ) {
                    this._testHitConnectionPoint( canvas, grp, mpos );
                }
            });
        }
    }

    //=========================================================================================
    // DEFCON_4-sensitive helper function used by event listeners
    //=========================================================================================
    _defcon4Filter( canvas, evt,  mpos ) {
        if (this.state === DEFCON_4) {

            // console.log(`_defcon4Filter`);
            const target = evt.target ? evt.target : evt;

            // Do not connect if it not over a yellow dot
            if ( !( (mpos.x >= this.destDot.xl && mpos.x <= this.destDot.xr &&
                mpos.y >= this.destDot.yt && mpos.y <= this.destDot.yb) ||
                (mpos.x >= this.srcDot.xl && mpos.x <= this.srcDot.xr &&
                mpos.y >= this.srcDot.yt && mpos.y <= this.srcDot.yb) ) ) {
                    this.srcGroupConnPending = undefined;
                }

            // We're in a line-draw situation where we flew over a destination dot's airspace.
            // Mouse-up should set DEFCON_4 and enter us here.
            // PF-2576: expect a mouse-up in order to proceed to DEFCON_4. Handle reconnect/line-move also.
            if ( this.srcGroupConnPending ) {
                if (this.srcGroup && this.destGroup
                    && this.isValidConnection(this.srcGroup, this.destGroup, this.reconnectingLine) ){

                    if (this.debug) { console.log(`_defcon4Filter begins to process ...`); }

                    const lineCopy = this.reconnectingLineModel;
                    const line = this._drawConnection(canvas, this.srcDot, this.destDot);

                    // Find src/dest widgets; record connection A and B end in them, respectively
                    if (lineCopy) {
                        line.model.attributes.text = lineCopy.attributes.text;
                    }
                    const newConnectionVO = this._recordConnection(canvas, line);
                    this.reconnectingLineModel = undefined;

                    // Label both lines of simple branch as first or second
                    // If first has been removed and second still exists, name new line as first
                    if (this.srcGroup.model.attributes.type === 'branchcommand') {
                          
                        let currentConn = this.srcGroup.connection.filter(c => (
                                                c.line.model.get("id") === line.model.get("id")))[0];

                        if (currentConn.end === constants.A_END) {                                  
                            let boolDisplay = () =>{                                    
                                let display = true;
                                const otherConn = this.srcGroup.connection.filter(c => (
                                            c.end === constants.A_END && c.line.model.get("id") !== currentConn.line.model.get("id")));
                
                                if(otherConn.length > 0){
                                    const existingConnBool = otherConn[0].line.model.get('branchBooleanDisplay');
                                    if(util.isTrue(existingConnBool)){
                                        display = false;
                                    }              
                                }     
                                else {
                                    const branchBool = currentConn.line.model.get('branchBooleanDisplay');
                                    display = branchBool ? util.isTrue(branchBool) : true;
                                }                              
                                return display;
                            };
                                
                            const displayBool = boolDisplay();                                
                            line.model.attributes.branchBooleanDisplay = displayBool.toString();

                            //PF-6374 Take the _id attribute first in case it is an existing connection that comes from flash
                            if (!displayBool) {                                   
                                this.srcGroup.model.attributes.falseConnector = line.model.get('_id') || line.model.get('id');
                            }
                            else {
                                this.srcGroup.model.attributes.trueConnector = line.model.get('_id') || line.model.get('id');
                            }                               
                        }
                    }
                    else{
                        //PF-6374 If is not a branch command remove the value from the attribute branchBooleanDisplay to avoid passing the True/False icon
                        line.model.attributes.branchBooleanDisplay = "";
                    }

                    const lineToFrom = {
                        srcGroup: this.srcGroup,
                        destGroup: this.destGroup,
                        left: line.left,
                        top: line.top,
                        width: line.width,
                        height: line.height
                    };

                    const mainKind = listService.mainKind;

                    undoService.add({
                        undo: () => {
                            switch (mainKind) {
                                case constants.KIND_BEHAVIORS:
                                    listService.destroyBehaviorConnnection(newConnectionVO.id);
                                    break;
                                case constants.KIND_WORKFLOW:
                                    listService.deleteWorkflowPath({id: newConnectionVO.id});
                                    break;
                            }
                            const lineObject = listService.getCanvasObjectById(newConnectionVO.id);
                            crudController._removeConnectionArrow(lineObject, newConnectionVO.id, mainKind);

                            // PF-4472: Refreshing canvas after deleting arrows.
                            listService.fcanvas.renderAll();
                        },
                        redo: () => {
                            switch (mainKind) {
                                case constants.KIND_BEHAVIORS:
                                    listService.addBehaviorConnection(newConnectionVO);
                                    // check current mainKind
                                    if (mainKind === constants.KIND_BEHAVIORS) {
                                        // May have to create new line here
                                        line.left = lineToFrom.left;
                                        line.top = lineToFrom.top;
                                        line.height = lineToFrom.height;
                                        line.width = lineToFrom.width;
                                        listService.fcanvas.add(line);
                                        line.setCoords();
                                        const srcObject = listService.getCanvasObjectById(lineToFrom.srcGroup.model.get('id'));
                                        const destObject = listService.getCanvasObjectById(lineToFrom.destGroup.model.get('id'));
                                        this.glueLineToEndPoints(srcObject, destObject, line, null);
                                    }
                                    break;
                                case constants.KIND_WORKFLOW:
                                    listService.addWorkflowPath(newConnectionVO);
                                    if (mainKind === constants.KIND_WORKFLOW) {
                                        line.left = lineToFrom.left;
                                        line.top = lineToFrom.top;
                                        line.height = lineToFrom.height;
                                        line.width = lineToFrom.width;
                                        listService.fcanvas.add(line);
                                        line.setCoords();
                                        const srcObject = listService.getCanvasObjectById(lineToFrom.srcGroup.model.get('id'));
                                        const destObject = listService.getCanvasObjectById(lineToFrom.destGroup.model.get('id'));
                                        this.glueLineToEndPoints(srcObject, destObject, line, null);
                                    }
                                    break;
                            }
                        }
                    }, textConstants.UNDO_NEW);

                    // Extinguish yellow dot.
                    this.srcGroupConnPending = undefined;
                    canvas.trigger('mouse:out', {target: target});

                    // PF-3561 Part of complaint was that caption was disappearing. This code disappeared it!
                    // newConnectionVO.model.set('text','',{silent:true});

                    // PF-3597 scenario 2: path-drawing issue
                    listService.currentSelectedPathId = newConnectionVO.model.get('id');
                }
                // State exit from DEFCON_4
                this.state = DEFCON_5;

                this.srcGroup.state = this.state;

                this.reconnectingLine = undefined;
                this.reconnectingEnd = undefined;

                // Made the connection to the dest connection point?
                if (target && target.type === 'group') {
                    // this.srcGroupConnPending = undefined;
                    this._lockMovement(target, false);
                    this._setStartState();
                }
                // Extinguish dot
                window.setTimeout( () => canvas.trigger('mouse:out', {target: target}), 0);

            }

            // console.log(`_defcon4Filter exit`, this.state);
        }
    }

    //=========================================================================================
    // DEFCON_6-sensitive reconnection filer function tried only at mouse-down
    // Captures initial connection source and destination values.
    // May set DEFCON_1 after marking target with DEFCON_6
    //=========================================================================================
    _defcon6Filter( canvas, evt ) {

        const target = evt.target;

        if(target && this.state === DEFCON_6) {
            if(this.reconnectingLine) {
                // console.log(`DEFCON_6 entered with reconnectingLine`, this.reconnectingLine );

                let connectionObject;
                let connectionLine;

                for(let connIndex in target.connection) {
                    let aConn = target.connection[connIndex];

                    if(aConn.line === this.reconnectingLine) {
                        connectionObject = aConn;
                        connectionLine = aConn.line;

                        // Get updated line point values
                        let points = connectionLine.calcLinePoints();

                        const p1 = new fabric.Point( connectionLine.get('x1'), connectionLine.get('y1'));
                        const p2 = new fabric.Point( connectionLine.get('x2'), connectionLine.get('y2'));

                        // console.log('p1', p1);
                        // console.log('p2', p2);
                        // console.log('left', connectionLine.left);
                        // console.log('top', connectionLine.top);


                        const widget1 = this.findWidgetById(connectionLine.srcId);
                        const widget2 = this.findWidgetById(connectionLine.destId);

                        const w1 = widget1;
                        const w2 = widget2;

                        let srcObject;
                        let destObject;

                        if(connectionObject.end === constants.A_END) {
                            console.log('A END');
                            this.reconnectingEnd = constants.A_END;
                            if(target === w1) {
                                console.log('Target is W1');
                                srcObject = w1;
                                destObject = w2;
                            } else {
                                console.log('Target is W2');
                                srcObject = w2;
                                destObject = w1;
                            }

                            this.reverseFlag = true;
                        } else {
                            console.log('B END');
                            this.reconnectingEnd = constants.B_END;
                            if(target === w1) {
                                console.log('Target is W1');
                                srcObject = w2;
                                destObject = w1;
                            } else {
                                console.log('Target is W2');
                                srcObject = w1;
                                destObject = w2;
                            }

                            this.reverseFlag = false;
                        }

                        this.srcGroup = srcObject;
                        this.destGroup = destObject;

                        // dest vs src
                        const srcGrpPos = { x: srcObject.left, y: srcObject.top };
                        const destGrpPos = { x: destObject.left, y: destObject.top };

                        const destIndex = Number(connectionLine.model.get('destDot'));
                        const srcIndex = Number(connectionLine.model.get('srcDot'));

                        this.srcDotObject = srcObject.getObjects()[srcIndex+1];
                        this.destDotObject = destObject.getObjects()[destIndex+1];

                        const srcWidth  = srcObject.getWidth();
                        const srcHeight = srcObject.getHeight();

                        const srcObjPos = {
                            xl: ( srcGrpPos.x + this.srcDotObject.left + srcWidth / 2 - 1 * this.connectionRadius ),
                            xr: ( srcGrpPos.x + this.srcDotObject.left + srcWidth / 2 + 4 * this.connectionRadius ),

                            yt: ( srcGrpPos.y + this.srcDotObject.top + srcHeight / 2 - 1 * this.connectionRadius ),
                            yb: ( srcGrpPos.y + this.srcDotObject.top + srcHeight / 2 + 4 * this.connectionRadius )
                        };

                        srcObjPos.dot = srcIndex;

                        const destWidth  = destObject.getWidth();
                        const destHeight = destObject.getHeight();

                        const destObjPos = {
                            xl: ( destGrpPos.x + this.destDotObject.left + destWidth / 2 - 1 * this.connectionRadius ),
                            xr: ( destGrpPos.x + this.destDotObject.left + destWidth / 2 + 4 * this.connectionRadius ),

                            yt: ( destGrpPos.y + this.destDotObject.top + destHeight / 2 - 1 * this.connectionRadius ),
                            yb: ( destGrpPos.y + this.destDotObject.top + destHeight / 2 + 4 * this.connectionRadius )
                        };

                        destObjPos.dot = destIndex;

                        // If not mouse down (this.srcDot) on one of the line's dots, then reset and break
                        if(!(srcObjPos.xl === this.srcDot.xl && srcObjPos.xr === this.srcDot.xr &&
                                srcObjPos.yt === this.srcDot.yt && srcObjPos.yb === this.srcDot.yb &&
                                srcObjPos.dot === this.srcDot.dot)
                            &&
                            !(destObjPos.xl === this.srcDot.xl && destObjPos.xr === this.srcDot.xr &&
                                destObjPos.yt === this.srcDot.yt && destObjPos.yb === this.srcDot.yb &&
                                destObjPos.dot === this.srcDot.dot)) {
                            // Reset
                            this.srcGroup = undefined;
                            this.destGroup = undefined;
                            this.reverseFlag = false;
                            this.srcDotObject = undefined;
                            this.destDotObject = undefined;
                            break; // or
                        }

                        this._lockMovement(target, true);
                        this.state = DEFCON_1;
                        target.state = DEFCON_6;

                        this.srcDot = srcObjPos;
                        this.destDot = destObjPos;

                        this.movedPast = false;

                        break;

                    }
                }
            }
        }
    }

    updateConnection(model, positionInfo) {
        // Helpers determine info for reacting to line slant during connected end movements
        const
            _isPlusHeight = line => line.get('y1') < line.get('y2'),
            _isPlusWidth = line => line.get('x1') < line.get('x2');

        // Moves an end of the line appropriate to slant and whether end A or B
        // Caller must determine those conditions, the call pair of appropriate move methods.

        const
            _movePlusHeight = (line, d) => line.set({'top': line.get('top')+d.dy, 'height': line.get('height')-d.dy}),
            _movePlusWidth = (line, d) => line.set({'width': line.get('width') + d.dx }),
            _moveNegHeight = (line, d) => line.set({'height': line.get('height') + d.dy }),
            _moveNegWidth = (line, d) => line.set({'left': line.get('left') + d.dx, 'width': line.get('width')-d.dx });

        const
            fcanvas = listService.fcanvas,
            obj = this.findWidgetById(model.get('id'));

        // If widget has connections, then for each connection point:
        // Update the list of line ends by pointer distance from last known position.
        //console.log(`moved obj`, obj);
        // Only adjust the connection lines if the widget group is not currently in a drawing state
        if (obj.connection && ((obj.state && fcanvas.states && obj.state === fcanvas.states.DEFCON_0) || (!obj.state && !obj.lockMovementX))) {

            // Object-moved delta
            const d = { dx: positionInfo.x1 - positionInfo.x2,  dy: positionInfo.y1 - positionInfo.y2 };

            // Move owned end of connections
            _.forEach( obj.connection, conn => {

                if (conn.end === constants.A_END) {
                    // Begin-drawing line - end

                    _isPlusHeight(conn.line) ? _movePlusHeight(conn.line, d) : _moveNegHeight(conn.line, d);
                    _isPlusWidth(conn.line) ? _moveNegWidth(conn.line, d) : _movePlusWidth(conn.line, d);

                    //console.log('adjusted conn A_End' + conn.line.model.get('srcDot') + ' by ' + JSON.stringify(d));

                } else if (conn.end === constants.B_END) {
                    // Ended-drawing line end: the arrow end.

                    //console.log('adjusted conn B_END' + conn.line.model.get('destDot') + ' by ' + JSON.stringify(d));
                    _isPlusHeight(conn.line) ? _moveNegHeight(conn.line, d) : _movePlusHeight(conn.line, d);
                    _isPlusWidth(conn.line) ? _movePlusWidth(conn.line, d) : _moveNegWidth(conn.line, d);

                }
                conn.line.setCoords();
            });
        }
        obj.prevPos = {'left': obj.left, 'top': obj.top};
        listService.fcanvas.renderAll();
    }


    //=================dock===============================================================================================
    // Returns true if and only if a workflow stage-to-stage connection is valid. Rule:
    //  a. Origin and destination are workflow stages
    //  b. There is no other mutual connection in the SAME DIRECTION.
    //
    // Return false otherwise
    //================================================================================================================
    isValidWorkFlowConnection(origin, dest, reconnecting) {
        const countOfExistingOriginToDestPaths = listService.workflowPaths.filter(
            c =>
                (c.srcCommand && c.srcCommand.id === origin.model.get('id')) &&
                (c.destCommand && c.destCommand.id === dest.model.get('id'))
        ).size;

        const rec = reconnecting === undefined? false : reconnecting;

        return (origin.model.get('factoryKey') === 'workflow_stage' &&
            dest.model.get('factoryKey') === 'workflow_stage') &&
            (!countOfExistingOriginToDestPaths || rec);
    }


    //================================================================================================================
    /*  Returns true if and only if the connection is valid.  Those are the rules:
        a. The exit command just can have connections in (connections out are not allowed)
        b. The start command just can have one connection out (connections in are not allowed)
        c. Simple branch can have 2 connections out (one for true, other for false) and any number of connecitons in
        d. Multiple branch can have as many connections as you want (in and out)
        7. Duplicated connections are not allowed (connections that have the same origin and same destination)
    */
    //================================================================================================================
    isValidConnection(origin, destination, reconnecting) {
        let result = true;
        let numberOfConnections = 0;
        // The most of the time we are moving the arrow (destination). The origin is the base of the arrow.
        //  We just move the origin sometimes when reconnecting. So, moveOrigin is false for default.
        let movingOrigin = false;  
        if (reconnecting) { // Are we moving the base of the arrow?
            movingOrigin = origin.model.get('id') !== reconnecting.srcId;
        }

        if (!origin || !destination) {
            result = false;
        }
        else if (origin.model.get('factoryKey') === 'workflow_stage'
            && destination.model.get('factoryKey') === 'workflow_stage') {
            result = this.isValidWorkFlowConnection(origin, destination, reconnecting);
        }
        else if (origin.model.get('factoryKey') === 'command_exit') {
            result = false;
        }
        else if(destination.model.get('factoryKey')==='command_start') {
            result = false;
        }
        else if(origin.model.get('factoryKey')==='command_simple_branch') {
            numberOfConnections = this.numberOfConnections(origin);
           if (reconnecting && movingOrigin && numberOfConnections >= 2 ) {
                result = false;
            } else if (!reconnecting && numberOfConnections > 1 ) {
                result = false;
            }
        }
        // Warning: negative catchall test. Keep it at end of if-ielse chain
        else if(origin.model.get('factoryKey')!=='command_multiple_branch') {
            numberOfConnections = this.numberOfConnections(origin);
            if (reconnecting && movingOrigin && numberOfConnections >= 1 ) {
                result = false;
            } else if (!reconnecting && numberOfConnections > 0 ) {
                result = false;
            }
        }


        if(result && !reconnecting && this.isDuplicatedConnection(origin, destination)) {
            result = false;
        }
        return result;
    }

    isDuplicatedConnection(origin, destination){
        let connections = listService.behaviorConnections;
        connections = connections.filter(c => (c.srcCommand && c.srcCommand.model.attributes.id === origin.model.get('id')
            && c.destCommand && c.destCommand.model.attributes.id === destination.model.get('id')) );
        return connections.size >= 1;
    }

    /* How many connections are coming out of a behavior?  */
    numberOfConnections(origin) {
        let connections = listService.behaviorConnections;
        connections = connections.filter(c => (c.srcCommand && c.srcCommand.model.attributes.id === origin.model.get('id') ));
        return connections.size;
    }
}

const connectionController = new ConnectionController();
export default connectionController;
